import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Input, Select, Space, Tag } from 'antd';
import { Sidebar } from '@/components/sidebar';
import { API } from '@/services/api.service';
import { date, money } from '@/app/suppliers/_components/format';
import { Facts } from '@/app/sef-state/_components/Filters';
import { ADDRESS_STATE_COLORS, STATUS_COLORS, addressStateOf, errorText } from './helpers';

const LOCKED = ['queued', 'sent', 'delivered', 'bounced', 'complaint'];

/** One partner: which addresses the mail goes to, or leaving them out. */
export default function RecipientSidebar({ companyId, requestId, recipient, onClose, onChanged, canWrite }) {
  const { t } = useTranslation('partnerRequests');
  const [picked, setPicked] = useState([]);
  const [typed, setTyped] = useState('');
  const [source, setSource] = useState('internet');
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    setPicked(recipient?.emails || []);
    setTyped('');
    setOverride(false);
    setReason(recipient?.excludedReason || '');
  }, [recipient]);

  const save = useMutation({
    mutationFn: (input) => API.partnerRequests.updateRecipient(companyId, requestId, recipient._id, input),
    onSuccess: () => onChanged(),
  });
  const reply = useMutation({
    mutationFn: (value) => API.partnerRequests.markReply(companyId, requestId, { ids: [recipient._id], reply: value }),
    onSuccess: () => onChanged(),
  });

  const addresses = useMemo(() => {
    const list = recipient?.candidates ? [...recipient.candidates] : [];
    (recipient?.emails || []).forEach((email) => {
      if (!list.some((c) => c.email === email)) list.push({ email, source: recipient.emailSource });
    });
    return list;
  }, [recipient]);

  if (!recipient) return <Sidebar open={false} onClose={onClose} title="" />;
  const locked = LOCKED.includes(recipient.status) || !canWrite;
  const bounced = save.error?.response?.data?.message === 'partner_request.address_bounced';
  const state = addressStateOf(recipient);

  const confirm = () => {
    const emails = [...picked, ...typed.split(/[;,\s]+/).filter(Boolean)];
    save.mutate({
      emails,
      ...(typed.trim() ? { source } : {}),
      ...(override ? { overrideBounce: true } : {}),
    });
  };

  return (
    <Sidebar title={recipient.name} open onClose={onClose} className="max-w-xl">
      <div className="grid gap-4 p-4">
        <Facts
          items={[
            [t('columns.pib'), recipient.pib || t('noPib')],
            [t('columns.registrationNo'), recipient.registrationNo],
            [t('columns.codes'), recipient.codes.join(', ')],
            [t('columns.accounts'), recipient.accounts.join(', ')],
            [t('columns.balance'), money(recipient.balance)],
            [t('columns.addressState'), <Tag key="s" color={ADDRESS_STATE_COLORS[state]}>{t(`addressStates.${state}`)}</Tag>],
            [t('columns.status'), <Tag key="st" color={STATUS_COLORS[recipient.status]}>{t(`statuses.${recipient.status}`)}</Tag>],
            recipient.error && [t('recipient.error'), t(`sendErrors.${recipient.error}`, { defaultValue: recipient.error })],
            recipient.sentAt && [t('columns.sentAt'), date(recipient.sentAt)],
            recipient.deadline && [t('recipient.deadline'), date(recipient.deadline)],
          ]}
        />

        <div>
          <div className="mb-2 font-semibold">{t('recipient.addresses')}</div>
          {!addresses.length && <p className="text-sm opacity-70">{t('recipient.noAddresses')}</p>}
          <Checkbox.Group className="grid gap-2" value={picked} disabled={locked} onChange={setPicked}>
            {addresses.map((c) => (
              <Checkbox key={c.email} value={c.email}>
                <span className="break-all">{c.email}</span>{' '}
                <Tag>{t(`sources.${c.source}`, { defaultValue: c.source })}</Tag>
                {c.invoices > 0 && (
                  <span className="text-xs opacity-60">
                    {t('recipient.invoices', { count: c.invoices, date: date(c.lastSeen) })}
                  </span>
                )}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>

        {!locked && (
          <>
            <div className="grid gap-2">
              <span className="text-sm">{t('recipient.addAddress')}</span>
              <Space.Compact className="w-full">
                <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="ime@firma.rs" />
                <Select
                  style={{ width: 130 }}
                  value={source}
                  onChange={setSource}
                  options={['internet', 'phone', 'manual'].map((s) => ({ value: s, label: t(`sources.${s}`) }))}
                />
              </Space.Compact>
            </div>
            {save.isError && <Alert type="error" showIcon message={errorText(t, save.error)} />}
            {bounced && (
              <Checkbox checked={override} onChange={(e) => setOverride(e.target.checked)}>
                {t('recipient.overrideBounce')}
              </Checkbox>
            )}
            <div className="flex justify-end">
              <Button
                shape="round"
                type="primary"
                loading={save.isPending}
                disabled={!picked.length && !typed.trim()}
                onClick={confirm}
              >
                {t('recipient.confirm')}
              </Button>
            </div>
            <div className="grid gap-2 border-t pt-3">
              {recipient.excluded ? (
                <div className="flex justify-end">
                  <Button shape="round" onClick={() => save.mutate({ excluded: false })}>
                    {t('recipient.include')}
                  </Button>
                </div>
              ) : (
                <>
                  <span className="text-sm">{t('recipient.excludeReason')}</span>
                  <Input value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
                  <div className="flex justify-end">
                    <Button
                      shape="round"
                      disabled={!reason.trim()}
                      onClick={() => save.mutate({ excluded: true, excludedReason: reason })}
                    >
                      {t('recipient.exclude')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {canWrite && recipient.sentAt && (
          <div className="grid gap-2 border-t pt-3">
            <span className="text-sm">{t('columns.reply')}</span>
            <Select
              value={recipient.reply || ''}
              loading={reply.isPending}
              onChange={(value) => reply.mutate(value)}
              options={['', 'replied', 'repliedWithAttachment'].map((value) => ({
                value,
                label: t(`replies.${value || 'none'}`),
              }))}
            />
          </div>
        )}
      </div>
    </Sidebar>
  );
}
